import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { mapPlaylistFromVideoSearch } from 'yankees-react-utils';
import ReportCard from '../../../../components/ReportCard';
import CatcherAccuracyChart from '../../../../components/CatcherAccuracyChart';
import CatcherTimesChart from '../../../../components/CatcherTimesChart';
import VideoPlayer from '../../../../components/VideoPlayer';
import ThrowingLog from './ThrowingLog';
import { store } from '../../../../store';

const StyledNavContainer = styled.div`
&& {
    margin-top: 47px;
    border-radius: .25rem;
    border: 1px solid #bcc8d1;

    .nav-tabs {
        margin-top: -37px;
        margin-left: 10px;
        min-height: 37px;
        a {
            border-radius: 5px 5px 0px 0px;
            cursor: pointer;
            display: block;
            padding: 8px 10px;
            border: 1px solid rgb(188, 200, 209);
            font-size: 13px;
            background: rgb(198, 211, 220) !important;
            color: rgb(69, 69, 69) !important;
            margin-right: 2.5px;
            text-align: center;
            white-space: nowrap;
        }

        a.active {
            font-weight: bold;
            pointer-events: none;
            background: white !important;
            border-bottom-color: white;
            color: rgb(50, 146, 255) !important;
            border-radius: 5px 5px 0px 0px;
            cursor: pointer;
            display: block;
            padding: 8px 10px;
            font-size: 13px;
            margin-right: 2.5px;
            text-align: center;
            white-space: nowrap;
        }
    }
}`;
const StyledContainer = styled.div`
    padding: 20px;
`;
const TabContent = styled.div`
    margin: 1px;
    padding: 10px;
    background-color: white;
    text-align: left;

`;

const getPitchVideo = (pitchLog = [], pitchInfo = {}, user) => {
    const { gameid, pitchseq, eventseq } = pitchInfo;

    const video = pitchLog.find((v) => v.gameid === gameid && v.pitchseq === pitchseq && v.eventseq === eventseq);

    return mapPlaylistFromVideoSearch({ videos: [video], playervideos: [] }, user.Roles.map((r) => r.RoleName));
};

const ThrowingCharts = ({ player, startYear, endYear, catcherThrows, catcherPickoffThrows }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const [activeTab, setActiveTab] = useState('throw-log');
    const [playlist, setPlaylist] = useState([]);

    return (
        <StyledNavContainer>
            <Tabs
                id={`throwing-charts-${player.PlayerID}`}
                activeKey={activeTab}
                onSelect={(a) => setActiveTab(a)}
            >
                <Tab eventKey="throw-log" title={translate('sections.reports.catcher.throwing.throw-log')}>
                    <TabContent>
                        <Row>
                            <Col sm={{ span: 12 }}>
                                <ReportCard title={`${translate('sections.reports.catcher.throwing.throw-log')} ${startYear === endYear ? startYear : `${startYear} - ${endYear}`}`}>
                                    <ThrowingLog throwLogData={catcherThrows} />
                                </ReportCard>
                            </Col>
                        </Row>
                    </TabContent>
                </Tab>
                <Tab eventKey="times-2B" title={translate('sections.reports.catcher.throwing.2b-times')}>
                    <TabContent>
                        <Row>
                            <Col xl={{ span: 4, order: 1 }} md={{ span: 12, order: 2 }}>
                                <StyledContainer>
                                    <CatcherTimesChart
                                        base={2}
                                        data={catcherThrows}
                                        onClick={(pitchInfo) => setPlaylist(getPitchVideo(catcherThrows, pitchInfo, user))}
                                    />
                                </StyledContainer>
                            </Col>
                            <Col xl={{ span: 8, order: 1 }} md={{ span: 12, order: 1 }}>
                                <StyledContainer>
                                    {playlist?.length ? <VideoPlayer playlist={playlist} maxHeight={640} /> : <h4>{translate('sections.reports.catcher.throwing.no-videos')}</h4>}
                                </StyledContainer>
                            </Col>
                        </Row>
                    </TabContent>
                </Tab>
                <Tab eventKey="times-3B" title={translate('sections.reports.catcher.throwing.3b-times')}>
                    <TabContent>
                        <Row>
                            <Col xl={{ span: 4, order: 1 }} md={{ span: 12, order: 2 }}>
                                <StyledContainer>
                                    <CatcherTimesChart
                                        base={3}
                                        data={catcherThrows}
                                        onClick={(pitchInfo) => setPlaylist(getPitchVideo(catcherThrows, pitchInfo, user))}
                                    />
                                </StyledContainer>
                            </Col>
                            <Col xl={{ span: 8, order: 1 }} md={{ span: 12, order: 1 }}>
                                <StyledContainer>
                                    {playlist?.length ? <VideoPlayer playlist={playlist} maxHeight={640} /> : <h4>{translate('sections.reports.catcher.throwing.no-videos')}</h4>}
                                </StyledContainer>
                            </Col>
                        </Row>
                    </TabContent>
                </Tab>
                <Tab eventKey="accuracy-2B" title={`${translate('baseball.2b')} ${translate('sections.reports.catcher.throwing.accuracy')}`}>
                    <TabContent>
                        <Row>
                            <Col xl={{ span: 4, order: 1 }} md={{ span: 12, order: 2 }}>
                                <StyledContainer>
                                    <CatcherAccuracyChart
                                        base={2}
                                        data={catcherThrows}
                                        onClick={(pitchInfo) => setPlaylist(getPitchVideo(catcherThrows, pitchInfo, user))}
                                    />
                                </StyledContainer>
                            </Col>
                            <Col xl={{ span: 8, order: 1 }} md={{ span: 12, order: 1 }}>
                                <StyledContainer>
                                    {playlist?.length ? <VideoPlayer playlist={playlist} maxHeight={640} /> : <h4>{translate('sections.reports.catcher.throwing.no-videos')}</h4>}
                                </StyledContainer>
                            </Col>
                        </Row>
                    </TabContent>
                </Tab>
                <Tab eventKey="accuracy-3B" title={`${translate('baseball.3b')} ${translate('sections.reports.catcher.throwing.accuracy')}`}>
                    <TabContent>
                        <Row>
                            <Col xl={{ span: 4, order: 1 }} md={{ span: 12, order: 2 }}>
                                <StyledContainer>
                                    <CatcherAccuracyChart
                                        base={3}
                                        data={catcherThrows}
                                        onClick={(pitchInfo) => setPlaylist(getPitchVideo(catcherThrows, pitchInfo, user))}
                                    />
                                </StyledContainer>
                            </Col>
                            <Col xl={{ span: 8, order: 1 }} md={{ span: 12, order: 1 }}>
                                <StyledContainer>
                                    {playlist?.length ? <VideoPlayer playlist={playlist} maxHeight={640} /> : <h4>{translate('sections.reports.catcher.throwing.no-videos')}</h4>}
                                </StyledContainer>
                            </Col>
                        </Row>
                    </TabContent>
                </Tab>
                <Tab eventKey="back-picks" title={translate('sections.reports.catcher.throwing.backpicks')}>
                    <TabContent>
                        <ThrowingLog isPickoff throwLogData={catcherPickoffThrows} />
                    </TabContent>
                </Tab>
            </Tabs>
        </StyledNavContainer>
    );
};

ThrowingCharts.propTypes = {
    player: PropTypes.shape({
        PlayerID: PropTypes.number,
        FullName: PropTypes.string,
    }).isRequired,
    startYear: PropTypes.number.isRequired,
    endYear: PropTypes.number.isRequired,
    catcherThrows: PropTypes.arrayOf(PropTypes.object).isRequired,
    catcherPickoffThrows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default ThrowingCharts;
